import { motion } from "framer-motion";
import { Section, Container, Eyebrow, Reveal, CTAButton } from "../components/site/primitives";
import { StatBand, SegmentCard, CTABanner } from "../components/site/blocks";
import { SEGMENT_LIST, HERO_IMG } from "../lib/content";
import Seo from "../components/site/Seo";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Community Tax",
  url: typeof window !== "undefined" ? window.location.origin : "",
  description:
    "Embedded tax debt resolution infrastructure for fintechs, lenders, tax software providers, law firms, and financial advisors.",
  contactPoint: {
    "@type": "ContactPoint",
    email: "partners@communitytax.com",
    contactType: "Partnerships",
  },
};

const homeStats = [
  { value: "130,000+", caption: "Clients served" },
  { value: "$2.3B+", caption: "Tax debt resolved" },
  { value: "48 states", caption: "Coverage across the U.S." },
  { value: "EN · ES", caption: "Full bilingual servicing" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Community Tax Enterprise Partnerships | Embedded Tax Debt Resolution"
        description="Turn hidden IRS tax debt into a revenue and retention engine. Embed Community Tax's resolution service into your platform — no headcount, no operational lift."
        path="/"
        jsonLd={orgJsonLd}
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-12 lg:pt-20 pb-10">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <Eyebrow>Embedded Tax Debt Resolution</Eyebrow>
                <h1 className="font-display font-extrabold text-[#1E1E24] text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight">
                  Turn Hidden Tax Debt Into a Scalable Revenue and Retention Engine
                </h1>
                <p className="mt-7 text-lg text-[#40424D] leading-relaxed max-w-xl">
                  Unresolved IRS tax debt quietly stalls loan transactions, derails tax filings, and
                  sends customers looking for help elsewhere. Community Tax resolves it behind the
                  scenes — so you keep the relationship, lift conversion, and protect revenue without
                  adding headcount or operational lift.
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <CTAButton to="/become-a-partner" variant="primary" testId="hero-become-partner">
                    Become a Partner
                  </CTAButton>
                  <CTAButton to="/how-it-works" variant="ghost" testId="hero-how-it-works">
                    See How It Works
                  </CTAButton>
                </div>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="relative"
            >
              <img
                src={HERO_IMG}
                alt="Abstract illustration of hidden friction being removed"
                className="w-full rounded-[28px] ct-shadow-card"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* STAT BAND */}
      <section className="py-12 lg:py-16 border-y border-slate-200/70 bg-white">
        <Container>
          <StatBand stats={homeStats} />
        </Container>
      </section>

      {/* POSITIONING SNAPSHOT */}
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>What We Do</Eyebrow>
          <Reveal>
            <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
              Tax debt is one of the most common — and least visible — disruptions in the financial
              ecosystem.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-[#40424D] leading-relaxed">
              Each year, millions of customers engage financial platforms, advisors, tax preparers,
              and lenders at critical moments where unresolved IRS tax debt blocks progress —
              resulting in filings completed with unresolved debt, loan denials or delays, liens or
              levies on assets, and ultimately customer drop-off.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-white rounded-[28px] ct-shadow-card p-8 lg:p-12 max-w-4xl">
            <h3 className="font-display text-2xl font-bold text-[#1E1E24] mb-4">
              Embedded Tax Debt Resolution Infrastructure
            </h3>
            <p className="text-[#40424D] leading-relaxed">
              Community Tax provides fully managed, embedded tax debt representation services that
              integrate seamlessly into partner workflows — helping tax software providers, fintechs,
              lenders, tax preparers, CPAs, and financial institutions remove IRS tax debt as a
              barrier to completing customer outcomes.
            </p>
            <p className="mt-5 text-[#1E1E24] font-medium leading-relaxed">
              Partners maintain full ownership of the client relationship, brand, and experience —
              while we resolve IRS tax debt behind the scenes.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* WHO WE SERVE TEASER */}
      <section className="pb-20 lg:pb-28">
        <Container>
          <div className="max-w-2xl mb-12">
            <Eyebrow>Who We Serve</Eyebrow>
            <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl leading-tight">
              Built for the platforms where tax debt breaks outcomes
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SEGMENT_LIST.map((s, i) => (
              <SegmentCard key={s.slug} segment={s} index={i} />
            ))}
          </div>
        </Container>
      </section>

      <CTABanner />
    </>
  );
}
